import React, { useState } from 'react';

import api, { BASE_URL, setToken } from '../../utils/api';
import { classNames } from '../../utils/utils';
import heroImage from './hero.png';

const inputClassName =
  'text-sm relative focus:z-20 appearance-none w-full px-3 py-3 text-gray-900 focus:outline-none leading-5 border-2 border-gray-400 placeholder:text-black placeholder:uppercase tracking-[2px] focus:border-black transition-colors ' +
  'classic:border classic:py-2 classic:tracking-normal classic:placeholder:normal-case classic:placeholder:text-gray-500 classic:focus:border-gray-600 ' +
  'split:border split:py-2 split:text-base split:tracking-normal split:placeholder:normal-case split:placeholder:text-gray-500 split:focus:border-gray-600';

const labelClassName = 'hidden split:block text-xs text-gray-800 uppercase mb-1';

const login = (onLogin, setBusy, setError, credentials) => {
  setBusy(true);

  api
    .post('/auth/login', { body: credentials })
    .then(({ access_token }) => {
      setToken(access_token);
      onLogin();
    })
    .catch(err => {
      if (err.status === 401) {
        setError('Uw gebruikersnaam of wachtwoord is incorrect.');
      } else if (err.status === 429) {
        setError('Te veel inlogpogingen. Probeer het later opnieuw');
      } else {
        setError('Er is iets fout gelopen. Probeer het later opnieuw');
      }

      setBusy(false);
    });
};

const Login = ({ onLogin, tenant }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState();
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="min-h-screen flex items-center justify-center split:gap-16">
      <div className="max-w-lg classic:max-w-md w-full py-12 px-6 split:md:w-1/2">
        <div className="flex justify-center split:justify-start">
          {tenant?.has_logo
            ? <img src={`${BASE_URL}/tenant/logo`} alt={`Logo ${tenant.name}`} className="w-42" />
            : <div className="text-3xl font-bold">{tenant?.name}</div>}
        </div>
        <p className="mt-8 leading-5 text-center text-black classic:mt-5 classic:text-gray-600 split:text-left split:font-light">
          Vul je logingegevens in om toegang te krijgen tot het fotoarchief.
          {tenant?.phone && (
            <> Problemen of hulp nodig? Bel <a href={`tel:${tenant.phone.replace(/[^\d+]/g, '')}`}>{tenant.phone}</a>.</>
          )}
        </p>
        <form className="mt-8 flex flex-col gap-6 classic:mt-6 classic:gap-0 split:gap-0">
          <label htmlFor="username" className={labelClassName}>Gebruikersnaam</label>
          <input
            id="username"
            className={classNames(
              inputClassName,
              'classic:rounded-t-md split:mb-4',
              error ? 'border-red-600' : 'border-gray-300'
            )}
            placeholder="Gebruikersnaam"
            type="text"
            name="username"
            value={username}
            onChange={e => setUsername(e.target.value)}
          />
          <label htmlFor="password" className={labelClassName}>Wachtwoord</label>
          <div className="relative classic:-mt-px split:mb-4">
            <input
              id="password"
              className={classNames(
                inputClassName,
                'classic:rounded-b-md',
                error ? 'border-red-600' : 'border-gray-300'
              )}
              placeholder="Wachtwoord"
              name="password"
              value={password}
              type={showPassword ? 'text' : 'password'}
              onChange={e => setPassword(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowPassword(prev => !prev)}
              className="absolute inset-y-0 right-3 flex items-center text-gray-900 hover:text-black cursor-pointer"
            >
              {showPassword ? (
                /* Eye off */
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                </svg>

              ) : (
                /* Eye */
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                </svg>

              )}
            </button>
          </div>
          {error && <p className="mt-1 mb-4 text-red-600">{error}</p>}

          <button
            type="button"
            disabled={busy}
            className="text-sm block w-full cursor-pointer border-2 py-3 uppercase spacing font-light tracking-[5px] group hover:bg-gray-100 hover:border-gray-100 transition-colors relative classic:mt-5 classic:py-2 classic:text-base classic:normal-case classic:tracking-normal classic:font-normal classic:rounded-md classic:border-0 classic:bg-gray-900 classic:text-white classic:hover:bg-gray-900 split:w-auto split:self-start split:border split:border-[#ddd] split:px-2 split:py-1 split:text-base split:normal-case split:tracking-normal split:hover:bg-transparent split:hover:border-[#ddd]"
            onClick={() => login(onLogin, setBusy, setError, { username, password })}
          >
            Inloggen
            <div className="absolute right-8 top-0 h-full flex items-center group-hover:right-4 transition-all classic:hidden split:hidden">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-5 opacity-0 group-hover:opacity-100 transition">
                <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
              </svg>
            </div>
          </button>
        </form>
      </div>
      <img src={heroImage} alt="" className="hidden split:md:block w-1/2 max-w-xl" />
    </div>
  );
};

Login.defaultProps = {
  onLogin: () => { },
};

export default Login;
